/**
 * LvHomeCanvas — tab zero's content: Home, and (from Phase 3) the canvas,
 * merged into ONE thing. The LV port of the Console's home/HomeCanvas.tsx.
 *
 * THE CONTRACT. Home gets the canned start — the owner-authored mini-site
 * (hero, tiles, the nav its pages carry). What shows in tab zero is not fixed:
 * anything projected onto the canvas REPLACES it, in place, in the same tab:
 *
 *   - an INFO cast replaces-and-stays: the projection covers Home until it is
 *     cleared, and then Home is back exactly as it was — loaded doc, Back
 *     trail, scroll position. That is why LvHomeSurface below stays
 *     MOUNTED-but-hidden under a projection (display:none) rather than
 *     unmounting: its state IS the start page's state, and a cast passing
 *     through must not throw it away.
 *   - an INPUT cast replaces-then-restores: the projection owns the tab while
 *     it gathers an answer, then clears itself and Home is revealed again.
 *   - the Home icon (shell chrome, Phase 3) returns the base: it clears any
 *     projection and reselects tab zero.
 *
 * Phase 2 ships the container and the projection state/API only — the cast
 * machinery itself is Phase 3. The context below is how a future projection
 * source (assistant bridge, a surface's request) displaces Home; today a
 * non-null projection renders a placeholder cover so the mount/hide mechanics
 * are real and testable.
 */
import { createContext, useContext, useMemo, useState } from 'react'
import { Box, Stack, Typography } from '@mui/material'
import LvHomeSurface from './LvHomeSurface'
import type { HomeNavLink } from './vendor/home/types'

/** What can displace Home in tab zero. `canvas` = a scene projection. */
export type LvHomeProjection = { kind: 'canvas'; sceneId: string }

export interface LvHomeProjectionContextValue {
  projection: LvHomeProjection | null
  setProjection: (p: LvHomeProjection | null) => void
}

export const LvHomeProjectionContext = createContext<LvHomeProjectionContextValue>({
  projection: null,
  setProjection: () => undefined,
})

/** Phase 3's cast machinery reads/drives the projection through this. */
export function useLvHomeProjection(): LvHomeProjectionContextValue {
  return useContext(LvHomeProjectionContext)
}

export default function LvHomeCanvas({
  offeredSurfaces,
  onOpenSurface,
  onNavChange,
  navFollowRef,
}: {
  offeredSurfaces: string[]
  onOpenSurface: (id: string, label?: string) => void
  /** The masthead's Explore channels — forwarded to HomeSurface unchanged. */
  onNavChange?: (items: HomeNavLink[], currentFile: string | undefined) => void
  navFollowRef?: { current: ((n: HomeNavLink) => void) | null }
}) {
  const [projection, setProjection] = useState<LvHomeProjection | null>(null)
  const ctx = useMemo(() => ({ projection, setProjection }), [projection])
  const projected = projection !== null
  return (
    <LvHomeProjectionContext.Provider value={ctx}>
      <Box sx={{ height: '100%' }}>
        {/* MOUNTED-but-hidden while a projection is up — see the contract above. */}
        <Box sx={{ height: '100%', display: projected ? 'none' : 'block' }}>
          <LvHomeSurface
            offeredSurfaces={offeredSurfaces} onOpenSurface={onOpenSurface}
            onNavChange={onNavChange} navFollowRef={navFollowRef}
          />
        </Box>
        {projection && (
          <Stack sx={{ height: '100%', alignItems: 'center', justifyContent: 'center' }} spacing={1}>
            <Typography variant="body2" color="text.secondary">
              Canvas projection ({projection.kind}: {projection.sceneId})
            </Typography>
            <Typography variant="caption" color="text.disabled">
              The cast machinery arrives in Phase 3 — this cover proves Home stays mounted beneath it.
            </Typography>
          </Stack>
        )}
      </Box>
    </LvHomeProjectionContext.Provider>
  )
}
